import { DestroyRef, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { SwProcessingService } from './sw-processing.service';
import { SwUpdateService } from './sw-update.service';

/**
 * Applies a pending update on the next page-to-page navigation, so most users
 * land on the new build without clicking anything. A navigation is a moment the
 * user has already chosen to leave the current screen.
 *
 * Renders nothing. The visible half is UpdateStripComponent's "Refresh now".
 */
@Injectable({ providedIn: 'root' })
export class SwUpdateNavigationService {
  /** Latches once the page is being replaced, so it never fires twice. */
  private applying = false;

  constructor(
    private swUpdateService: SwUpdateService,
    private swProcessingService: SwProcessingService,
    private router: Router,
    private destroyRef: DestroyRef
  ) {}

  /**
   * Starts watching navigations. Called once from an APP_INITIALIZER.
   *
   * @returns Nothing.
   */
  start(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => this.onNavigationStart(event));
  }

  /**
   * Replaces the page with the new build when an update is pending and it is
   * safe to do so. Otherwise the update stays pending for the next navigation,
   * and the "Refresh now" strip stays on screen.
   *
   * @param event - The navigation that just started.
   * @returns Nothing.
   */
  private onNavigationStart(event: NavigationStart): void {
    if (!this.swUpdateService.updateAvailable() || this.applying) { return; }

    // Re-entering the same route is not a screen change; reloading there would
    // look like the page reloading by itself.
    if (this.stripFragment(event.url) === this.stripFragment(this.router.url)) { return; }

    // Never reload while an API call or contract transaction is running, or with
    // no network (the new build could not be fetched).
    if (this.swProcessingService.isProcessing() || this.isOffline()) { return; }

    this.applying = true;
    void this.swUpdateService.activateAndNavigate(event.url);
  }

  /**
   * @param url - A router URL.
   * @returns The URL without its fragment.
   */
  private stripFragment(url: string): string {
    return url.split('#')[0];
  }

  /**
   * `onLine === false` is a reliable negative; `true` only means "has a link".
   *
   * @returns True when the browser reports no network.
   */
  private isOffline(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  }
}
